import React, { useEffect, useState } from 'react';

const V1 = '#7F00FF', V2 = '#B163FF', TEAL = '#3B82F6', GREEN = '#2ECC71', RED = '#F04155';
const MIN_SIZE = 4, MAX_SIZE = 12;
const MODES = [
  { key: 'fixed', label: 'Fixed Window Sum' },
  { key: 'minlen', label: 'Smallest Subarray ≥ Target' },
  { key: 'kdistinct', label: 'K Distinct Characters' },
  { key: 'flipzeros', label: 'Flip At Most K Zeros' },
  { key: 'quiz', label: 'Quick Quiz' },
];

const emptyArray = n => Array.from({ length: n }, () => null);

const CodePanel = ({ title, lines, active }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ padding: '9px 14px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
      {title || 'Java'}
    </div>
    <pre style={{ margin: 0, padding: '12px 0', background: '#0E0B18', fontFamily: "'Fira Code','Consolas',monospace", fontSize: 12.5, lineHeight: 1.9, overflowX: 'auto' }}>
      {lines.map((line, i) => (
        <div key={i} style={{
          padding: '0 16px', color: i === active ? '#fff' : '#B7ACD6',
          background: i === active ? `${V1}45` : 'transparent',
          borderLeft: `3px solid ${i === active ? V2 : 'transparent'}`,
          whiteSpace: 'pre',
        }}>{line || ' '}</div>
      ))}
    </pre>
  </div>
);

/* ─── Box row that highlights a contiguous [left, right] window ────── */
const WindowRow = ({ array, left, right, badColor }) => (
  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 26, paddingBottom: 22 }}>
    {array.map((val, i) => {
      const inWindow = left !== null && right !== null && i >= left && i <= right;
      const bad = badColor && inWindow && badColor.includes(i);
      return (
        <div key={i} style={{ position: 'relative' }}>
          {i === left && <span style={{ position: 'absolute', top: -22, left: '50%', transform: 'translateX(-50%)', fontSize: 9.5, fontWeight: 800, color: V1, whiteSpace: 'nowrap' }}>left</span>}
          {i === right && <span style={{ position: 'absolute', top: -22, left: '50%', transform: 'translateX(-50%)', fontSize: 9.5, fontWeight: 800, color: V2, whiteSpace: 'nowrap' }}>right</span>}
          <div style={{
            width: 46, height: 46, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 15, fontWeight: 800, fontFamily: "'Inter',sans-serif",
            background: bad ? `linear-gradient(135deg,${RED},#c0293a)` : inWindow ? `linear-gradient(135deg,${V1},${V2})` : 'var(--bg3)',
            color: inWindow ? '#fff' : 'var(--text)',
            border: `1.5px solid ${bad ? RED : inWindow ? V1 : 'var(--border)'}`, transition: 'all 0.2s ease',
          }}>{val === null || val === undefined ? '·' : val}</div>
          <span style={{ position: 'absolute', left: 0, right: 0, textAlign: 'center', bottom: -18, fontSize: 10, fontWeight: 700, color: 'var(--muted)' }}>{i}</span>
        </div>
      );
    })}
  </div>
);

const Chip = ({ children, color = V1 }) => (
  <span style={{
    display: 'inline-flex', padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 700,
    background: `${color}15`, color, marginRight: 6, marginBottom: 6, fontFamily: 'monospace',
  }}>{children}</span>
);

const smallInput = {
  width: 90, padding: '8px 10px', borderRadius: 9, border: '1px solid var(--border)',
  background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
};
const wideInput = { ...smallInput, width: 220 };
const smallBtn = (bg = V1, disabled = false) => ({
  padding: '8px 14px', borderRadius: 9, border: 'none', cursor: disabled ? 'default' : 'pointer',
  background: disabled ? 'var(--bg3)' : bg, color: disabled ? 'var(--muted)' : '#fff',
  fontSize: 12.5, fontWeight: 700, fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const ghostBtn = disabled => ({
  padding: '8px 14px', borderRadius: 9, cursor: disabled ? 'default' : 'pointer',
  border: `1.5px solid ${disabled ? 'var(--border)' : V1}`, background: 'transparent',
  color: disabled ? 'var(--muted)' : V1, fontSize: 12.5, fontWeight: 700,
  fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const StepNav = ({ step, total, onPrev, onNext, playing, onPlay }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
    <button onClick={onPrev} disabled={step === 0} style={ghostBtn(step === 0)}>← Prev</button>
    <button onClick={onNext} disabled={step === total - 1} style={smallBtn(V1, step === total - 1)}>Next →</button>
    {onPlay && <button onClick={onPlay} style={smallBtn(playing ? RED : GREEN)}>{playing ? 'Pause' : 'Auto Play'}</button>}
    <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Step {step + 1} of {total}</span>
  </div>
);

/* ─── Fixed Window Sum (Max Sum Subarray of Size K) ─────────────────── */
const FixedWindowMode = () => {
  const [sizeInput, setSizeInput] = useState('7');
  const [array, setArray] = useState(emptyArray(7));
  const [insertValue, setInsertValue] = useState('');
  const [kInput, setKInput] = useState('3');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const filledCount = array.filter(v => v !== null).length;
  const isFull = filledCount === array.length;

  const createArray = () => {
    const n = Math.min(MAX_SIZE, Math.max(MIN_SIZE, parseInt(sizeInput, 10) || MIN_SIZE));
    setArray(emptyArray(n)); setSteps(null); setStep(0); setPlaying(false);
  };
  const insertNext = () => {
    const idx = array.findIndex(v => v === null);
    if (idx === -1 || insertValue.trim() === '' || isNaN(Number(insertValue))) return;
    const next = [...array]; next[idx] = Number(insertValue); setArray(next);
    setInsertValue('');
  };
  const startSlide = () => {
    if (!isFull) return;
    const k = Math.min(array.length, Math.max(1, parseInt(kInput, 10) || 1));
    let sum = array.slice(0, k).reduce((a, b) => a + b, 0);
    const built = [{ left: 0, right: k - 1, sum, max: sum }];
    let max = sum;
    for (let right = k; right < array.length; right++) {
      const left = right - k + 1;
      sum = sum - array[left - 1] + array[right];
      max = Math.max(max, sum);
      built.push({ left, right, sum, max, outgoing: array[left - 1], incoming: array[right] });
    }
    setSteps(built); setStep(0); setPlaying(false);
  };

  useEffect(() => {
    if (!playing || !steps) return;
    if (step >= steps.length - 1) { setPlaying(false); return; }
    const t = setTimeout(() => setStep(s => s + 1), 950);
    return () => clearTimeout(t);
  }, [playing, step, steps]);

  const cur = steps ? steps[step] : null;
  const note = !cur ? 'Build an array, set k, then Slide Window.' :
    step === 0 ? `Initial window [0, ${cur.right}]: sum = ${cur.sum}.` :
    `Slide right: subtract outgoing arr[${cur.left - 1}]=${cur.outgoing} (drops out the left), add incoming arr[${cur.right}]=${cur.incoming} (enters from the right). New sum = ${cur.sum}. O(1) update — no rescanning the whole window.`;

  const code = {
    lines: [
      'int sum = 0;',
      'for (int i = 0; i < k; i++) sum += arr[i];',
      'int max = sum;',
      'for (int right = k; right < arr.length; right++) {',
      '    int left = right - k + 1;',
      '    sum = sum - arr[left - 1] + arr[right];',
      '    max = Math.max(max, sum);',
      '}',
    ],
    active: !cur ? -1 : step === 0 ? 1 : 5,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <WindowRow array={array} left={cur ? cur.left : null} right={cur ? cur.right : null} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)', minWidth: 60 }}>Size</span>
            <input value={sizeInput} onChange={e => setSizeInput(e.target.value)} style={smallInput} placeholder="7" />
            <button onClick={createArray} style={smallBtn(V1)}>Create Array</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)', minWidth: 60 }}>Insert</span>
            <input value={insertValue} onChange={e => setInsertValue(e.target.value)} style={smallInput} placeholder="value" disabled={isFull} />
            <button onClick={insertNext} style={smallBtn(V1, isFull)} disabled={isFull}>{isFull ? 'Full' : 'Insert'}</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)', minWidth: 60 }}>k</span>
            <input value={kInput} onChange={e => setKInput(e.target.value)} style={{ ...smallInput, width: 60 }} placeholder="3" />
            <button onClick={startSlide} style={smallBtn(GREEN, !isFull)} disabled={!isFull}>Slide Window</button>
          </div>
          {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} playing={playing} onPlay={() => setPlaying(p => !p)} />}
          {cur && <div><Chip color={V2}>sum = {cur.sum}</Chip><Chip color={GREEN}>max so far = {cur.max}</Chip></div>}
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>{note}</p>
        </div>
      </div>
      <CodePanel title="Java — Fixed-Size Window" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Smallest Subarray With Sum ≥ Target (Variable Window) ─────────── */
const MinLenMode = () => {
  const [arrInput, setArrInput] = useState('2, 1, 5, 2, 3, 2');
  const [targetInput, setTargetInput] = useState('7');
  const [arr, setArr] = useState([2, 1, 5, 2, 3, 2]);
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const startFind = () => {
    const parsed = arrInput.split(',').map(s => Number(s.trim())).filter(n => !isNaN(n));
    const target = Number(targetInput);
    if (parsed.length === 0 || isNaN(target)) return;
    const built = [];
    let left = 0, sum = 0, minLen = Infinity;
    for (let right = 0; right < parsed.length; right++) {
      sum += parsed[right];
      built.push({ left, right, sum, minLen, action: 'expand' });
      while (sum >= target) {
        minLen = Math.min(minLen, right - left + 1);
        built.push({ left, right, sum, minLen, action: 'record' });
        sum -= parsed[left];
        left++;
        built.push({ left, right, sum, minLen, action: 'shrink' });
      }
    }
    built.push({ done: true, minLen });
    setArr(parsed);
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const note = !cur ? 'Enter an array and a target sum, then Find Smallest.' :
    cur.done ? (cur.minLen === Infinity ? 'No window ever reached the target sum.' : `Smallest window length found: ${cur.minLen}.`) :
    cur.action === 'expand' ? `right expands to include arr[${cur.right}]. sum = ${cur.sum}.` :
    cur.action === 'record' ? `sum (${cur.sum}) ≥ target — window [${cur.left}, ${cur.right}] has length ${cur.right - cur.left + 1}. Record it, then try shrinking from the left.` :
    `Shrink: subtract arr[${cur.left - 1}] and move left forward to ${cur.left}. sum = ${cur.sum}.`;

  const code = {
    lines: [
      'int left = 0, sum = 0, minLen = Integer.MAX_VALUE;',
      'for (int right = 0; right < arr.length; right++) {',
      '    sum += arr[right];',
      '    while (sum >= target) {',
      '        minLen = Math.min(minLen, right - left + 1);',
      '        sum -= arr[left];',
      '        left++;',
      '    }',
      '}',
    ],
    active: !cur ? -1 : cur.done ? -1 : cur.action === 'expand' ? 2 : cur.action === 'record' ? 4 : 6,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <WindowRow array={arr} left={cur && !cur.done ? cur.left : null} right={cur && !cur.done ? cur.right : null} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>Array</span>
            <input value={arrInput} onChange={e => { setArrInput(e.target.value); setSteps(null); }} style={wideInput} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>Target</span>
            <input value={targetInput} onChange={e => { setTargetInput(e.target.value); setSteps(null); }} style={smallInput} />
            <button onClick={startFind} style={smallBtn(V1)}>Find Smallest</button>
          </div>
          {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
          {cur && !cur.done && <div><Chip color={V2}>sum = {cur.sum}</Chip><Chip color={GREEN}>min length = {cur.minLen === Infinity ? '—' : cur.minLen}</Chip></div>}
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>{note}</p>
        </div>
      </div>
      <CodePanel title="Java — Variable Window (Shrink)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Longest Substring With At Most K Distinct Characters ──────────── */
const KDistinctMode = () => {
  const [text, setText] = useState('eceba');
  const [kInput, setKInput] = useState('2');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const startFind = () => {
    const s = text.trim();
    const k = Math.max(1, parseInt(kInput, 10) || 1);
    if (!s) return;
    const built = [];
    let left = 0, maxLen = 0;
    const freq = {};
    for (let right = 0; right < s.length; right++) {
      const c = s[right];
      freq[c] = (freq[c] || 0) + 1;
      built.push({ left, right, freq: { ...freq }, maxLen, action: 'expand', char: c });
      while (Object.keys(freq).length > k) {
        const lc = s[left];
        freq[lc]--;
        if (freq[lc] === 0) delete freq[lc];
        left++;
        built.push({ left, right, freq: { ...freq }, maxLen, action: 'shrink', char: lc });
      }
      maxLen = Math.max(maxLen, right - left + 1);
      built[built.length - 1].maxLen = maxLen;
    }
    built.push({ done: true, maxLen });
    setSteps(built); setStep(0);
  };

  const chars = text.trim().split('');
  const cur = steps ? steps[step] : null;
  const note = !cur ? 'Enter a string and k, then Find Longest.' :
    cur.done ? `Longest substring with at most ${kInput} distinct characters: length ${cur.maxLen}.` :
    cur.action === 'expand' ? `right expands to '${cur.char}'. Distinct characters in window: ${Object.keys(cur.freq).length}.` :
    `Too many distinct characters — shrink: remove '${cur.char}' from the left, left moves to ${cur.left}.`;

  const code = {
    lines: [
      'Map<Character, Integer> freq = new HashMap<>();',
      'int left = 0, maxLen = 0;',
      'for (int right = 0; right < s.length(); right++) {',
      '    char c = s.charAt(right);',
      '    freq.put(c, freq.getOrDefault(c, 0) + 1);',
      '    while (freq.size() > k) {',
      '        char lc = s.charAt(left);',
      '        freq.put(lc, freq.get(lc) - 1);',
      '        if (freq.get(lc) == 0) freq.remove(lc);',
      '        left++;',
      '    }',
      '    maxLen = Math.max(maxLen, right - left + 1);',
      '}',
    ],
    active: !cur ? -1 : cur.done ? -1 : cur.action === 'expand' ? 4 : 8,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <WindowRow array={chars} left={cur && !cur.done ? cur.left : null} right={cur && !cur.done ? cur.right : null} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>String</span>
            <input value={text} onChange={e => { setText(e.target.value); setSteps(null); }} style={wideInput} placeholder="eceba" />
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>k</span>
            <input value={kInput} onChange={e => { setKInput(e.target.value); setSteps(null); }} style={{ ...smallInput, width: 50 }} />
            <button onClick={startFind} style={smallBtn(V1)}>Find Longest</button>
          </div>
          {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
          {cur && !cur.done && (
            <div>
              {Object.entries(cur.freq).map(([k2, v]) => <Chip key={k2} color={TEAL}>{k2}: {v}</Chip>)}
              <Chip color={GREEN}>max length = {cur.maxLen}</Chip>
            </div>
          )}
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>{note}</p>
        </div>
      </div>
      <CodePanel title="Java — Window + Frequency Map" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Max Consecutive Ones After Flipping At Most K Zeros ───────────── */
const FlipZerosMode = () => {
  const [arrInput, setArrInput] = useState('1,1,0,0,1,1,1,0,1,1');
  const [kInput, setKInput] = useState('2');
  const [arr, setArr] = useState([1, 1, 0, 0, 1, 1, 1, 0, 1, 1]);
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const startFind = () => {
    const parsed = arrInput.split(',').map(s => Number(s.trim())).filter(n => !isNaN(n));
    const k = Math.max(0, parseInt(kInput, 10) || 0);
    if (parsed.length === 0) return;
    const built = [];
    let left = 0, zeros = 0, maxLen = 0;
    for (let right = 0; right < parsed.length; right++) {
      if (parsed[right] === 0) zeros++;
      built.push({ left, right, zeros, maxLen, action: 'expand' });
      while (zeros > k) {
        if (parsed[left] === 0) zeros--;
        left++;
        built.push({ left, right, zeros, maxLen, action: 'shrink' });
      }
      maxLen = Math.max(maxLen, right - left + 1);
      built[built.length - 1].maxLen = maxLen;
    }
    built.push({ done: true, maxLen });
    setArr(parsed);
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const note = !cur ? 'Enter a 0/1 array and k (max flips), then Find Longest.' :
    cur.done ? `Longest run of 1s after flipping at most ${kInput} zeros: length ${cur.maxLen}.` :
    cur.action === 'expand' ? `right expands. Zeros currently inside the window: ${cur.zeros} (budget: ${kInput}).` :
    `Too many zeros for the budget — shrink from the left, left moves to ${cur.left}.`;

  const code = {
    lines: [
      'int left = 0, zeros = 0, maxLen = 0;',
      'for (int right = 0; right < arr.length; right++) {',
      '    if (arr[right] == 0) zeros++;',
      '    while (zeros > k) {',
      '        if (arr[left] == 0) zeros--;',
      '        left++;',
      '    }',
      '    maxLen = Math.max(maxLen, right - left + 1);',
      '}',
    ],
    active: !cur ? -1 : cur.done ? -1 : cur.action === 'expand' ? 2 : 5,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <WindowRow array={arr} left={cur && !cur.done ? cur.left : null} right={cur && !cur.done ? cur.right : null} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>Array (0/1)</span>
            <input value={arrInput} onChange={e => { setArrInput(e.target.value); setSteps(null); }} style={wideInput} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>k (flips)</span>
            <input value={kInput} onChange={e => { setKInput(e.target.value); setSteps(null); }} style={{ ...smallInput, width: 60 }} />
            <button onClick={startFind} style={smallBtn(V1)}>Find Longest</button>
          </div>
          {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
          {cur && !cur.done && <div><Chip color={V2}>zeros in window = {cur.zeros}</Chip><Chip color={GREEN}>max length = {cur.maxLen}</Chip></div>}
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>{note}</p>
        </div>
      </div>
      <CodePanel title="Java — Window With a Counter" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Quick Quiz ─────────────────────────────────────────────────── */
const QUIZ_ARR = [4, 2, 1, 7, 8, 1, 2, 8, 1];
const buildQuizScenario = () => {
  const target = 8 + Math.floor(Math.random() * 8);
  const left = Math.floor(Math.random() * 4);
  const right = left + 1 + Math.floor(Math.random() * 3);
  const sum = QUIZ_ARR.slice(left, right + 1).reduce((a, b) => a + b, 0);
  const correct = sum >= target ? 'shrink' : 'expand';
  return { left, right, target, sum, correct };
};
const QuizMode = () => {
  const [scenario, setScenario] = useState(buildQuizScenario());
  const [answer, setAnswer] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });

  const pick = choice => {
    if (answer !== null) return;
    setAnswer(choice);
    setScore(s => ({ correct: s.correct + (choice === scenario.correct ? 1 : 0), total: s.total + 1 }));
  };
  const next = () => { setScenario(buildQuizScenario()); setAnswer(null); };

  const options = [
    { key: 'expand', label: 'Sum is below target — expand right forward' },
    { key: 'shrink', label: 'Sum already meets target — shrink left forward to look for something smaller' },
  ];

  return (
    <div>
      <WindowRow array={QUIZ_ARR} left={scenario.left} right={scenario.right} />
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 14 }}>
        target = {scenario.target}. Current window sum = {scenario.sum}. What should happen next?
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
        {options.map(o => {
          const revealed = answer !== null;
          const isCorrect = o.key === scenario.correct;
          const isPicked = o.key === answer;
          let border = 'var(--border)', bg = 'transparent', color = 'var(--text)';
          if (revealed && isCorrect) { border = GREEN; bg = 'rgba(46,204,113,0.1)'; color = '#1e9e57'; }
          else if (revealed && isPicked && !isCorrect) { border = RED; bg = 'rgba(240,65,85,0.08)'; color = RED; }
          return (
            <button key={o.key} onClick={() => pick(o.key)} disabled={revealed} style={{
              textAlign: 'left', padding: '10px 14px', borderRadius: 10,
              border: `1.5px solid ${border}`, background: bg, color, fontSize: 13.5,
              cursor: revealed ? 'default' : 'pointer', fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}>{o.label}</button>
          );
        })}
      </div>
      {answer !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={next} style={smallBtn(V1)}>Next Question</button>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Score: {score.correct} / {score.total}</span>
        </div>
      )}
    </div>
  );
};

/* ─── Main playground ────────────────────────────────────────────── */
const SlidingWindowPlayground = () => {
  const [mode, setMode] = useState('fixed');

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {MODES.map(m => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              padding: '8px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              border: `1.5px solid ${mode === m.key ? V1 : 'var(--border)'}`,
              background: mode === m.key ? V1 : 'transparent',
              color: mode === m.key ? '#fff' : 'var(--muted)',
              fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}
          >{m.label}</button>
        ))}
      </div>

      {mode === 'fixed' && <FixedWindowMode />}
      {mode === 'minlen' && <MinLenMode />}
      {mode === 'kdistinct' && <KDistinctMode />}
      {mode === 'flipzeros' && <FlipZerosMode />}
      {mode === 'quiz' && <QuizMode />}

      <style>{`
        @media (max-width: 640px) {
          .dsa-playground-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default SlidingWindowPlayground;
